import { CenterItem, Column, Row } from '@components/LinearLayout';
import {
  HoverInput,
  MobileVideoDetails,
  PlayerButton,
  ProgressRow,
  SliderWrapper,
  VideoControls,
  VideoDetails,
  VideoPlayerContainer,
  VideoTitle,
  VideosControlsTablet,
  VolumeIconWrapper,
} from '@composites/ImpressionsSection/styles';
import { useMobileWidth, useTabletWidth } from '@utils/hooks';
import {
  ChangeEvent,
  MouseEventHandler,
  TouchEvent,
  TouchEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Icon from '@components/Icon';
import { PSmall } from '@components/Typography';
import LoadingFallBack from '@routes/layouts/components/LoadingFallback';
import { formatDuration } from '@utils/time';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';
import screenfull from 'screenfull';
import { useTheme } from 'styled-components';
import { NoEvents } from '../EventsSection/styles';

interface VideoPlayerProps {
  isLoading: boolean;
  videoItems: Array<{ id: string; link: string; title: string }>;
  selectedVideo: string;
  textColor?: string;
  onPlayCallback?: () => void;
}

const VideoPlayer = ({
  isLoading,
  videoItems,
  selectedVideo,
  textColor,
  onPlayCallback,
}: VideoPlayerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = useTabletWidth();
  const isMobile = useMobileWidth();
  const [showVideoDetails, setShowVideoDetails] = useState<boolean>(false);
  const videoRef = useRef<ReactPlayer | null>(null);
  const videoWrapperRef = useRef<HTMLDivElement | null>(null);
  const [playPauseIsHovered, setPlayPauseIsHovered] = useState<boolean>(false);
  const [fullscreenIsHovered, setFullscreenIsHovered] =
    useState<boolean>(false);
  const [playing, setPlaying] = useState<boolean>(true);
  const [played, setPlayed] = useState<number>(0);
  const [volume, setVolume] = useState<number>(0);
  const [seeking, setSeeking] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const isIos =
    navigator.userAgent.includes('iPhone') &&
    !navigator.userAgent.includes('Macintosh');

  const selectedVideoItem = useMemo(
    () => videoItems?.find(e => e.id === selectedVideo),
    [selectedVideo, videoItems]
  );

  const handleVolumeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.target.value));
  };

  const handleVolumeTouchChange = (e: TouchEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.currentTarget.value));
  };

  const handleSeekMouseDown = () => {
    setSeeking(true);
  };
  const handleSeekChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPlayed(parseFloat(e.target.value));
  };

  const handleSeekTouchChange = (e: TouchEvent<HTMLInputElement>) => {
    setPlayed(parseFloat(e.currentTarget.value));
  };

  const handleSeekMouseUp: MouseEventHandler<HTMLInputElement> = e => {
    const videoElement = videoRef?.current;
    setSeeking(false);
    videoElement?.seekTo(parseFloat((e.target as HTMLInputElement).value));
  };

  const handleSeekTouchUp: TouchEventHandler<HTMLInputElement> = e => {
    const videoElement = videoRef?.current;
    setSeeking(false);
    videoElement?.seekTo(parseFloat((e.target as HTMLInputElement).value));
  };

  const handleProgress = (state: OnProgressProps) => {
    if (!seeking) {
      setPlayed(state.played);
    }
  };

  const getYouTubeVideoId = (url: string): string | null => {
    const match = url.match(/v=([^&]+)/);
    const videoId = match ? match[1] : null;

    return videoId;
  };

  const getYouTubeThumbnail = (video: {
    id: string;
    link: string;
    title: string;
  }) => {
    try {
      const youtubeLinkId = getYouTubeVideoId(video.link);

      if (youtubeLinkId) {
        return `https://img.youtube.com/vi/${youtubeLinkId}/sddefault.jpg`; // Get default thumbnail
      } else {
        return true;
      }
    } catch (e) {
      return true;
    }
  };

  const volumeIconName = useMemo(() => {
    if (volume === 0) {
      return 'event-pages_mute';
    } else if (volume > 0 && volume < 0.5) {
      return 'event-pages_speaker-low';
    } else if (volume > 0.5) {
      return 'event-pages_speaker-high';
    }
    return 'event-pages_mute';
  }, [volume]);

  const handleClickFullscreen = useCallback(() => {
    const videoElement = videoWrapperRef?.current;
    if (screenfull.isEnabled) {
      if (videoElement != null)
        void screenfull
          .toggle(videoElement, { navigationUI: 'hide' })
          .then(() => {
            (videoElement as Element).scrollIntoView();
          });
    }
    if (videoElement != null) {
      if (!isFullscreen && isIos) {
        videoElement.style.position = 'fixed';
        videoElement.style.top = '0';
        videoElement.style.left = '0';
        videoElement.style.width = '100%';
        videoElement.style.height = '100%';
        videoElement.style.zIndex = '10100';
        document.body.style.overflow = 'hidden';
        setIsFullscreen(true);
      } else {
        videoElement.style.position = 'relative';
        videoElement.style.width = '100%';
        videoElement.style.height = 'auto';
        videoElement.style.display = 'flex';
        videoElement.style.flexDirection = 'column';
        videoElement.style.paddingTop = '56.25%';
        videoElement.style.width = '95%';
        videoElement.style.margin = 'auto';
        document.body.style.overflow = 'hidden';
        setIsFullscreen(false);
      }
    }
  }, [isFullscreen, isIos]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const videoElement = videoWrapperRef?.current;
      if (
        (event.key === 'Escape' || event.key === 'Esc') &&
        videoElement != null &&
        isFullscreen &&
        isIos
      ) {
        videoElement.style.position = 'relative';
        videoElement.style.width = 'auto';
        videoElement.style.height = 'auto';
        document.body.style.overflow = '';
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFullscreen, isIos]);

  const videoControls = useMemo(() => {
    return (
      <Column $gap="0.5rem">
        <ProgressRow>
          <Row $gap="0.2rem">
            <VolumeIconWrapper
              onClick={() => {
                setVolume(volume === 0 ? 1 : 0);
              }}
            >
              <Icon
                size={24}
                name={volumeIconName}
                color={theme.color.primary}
              />
            </VolumeIconWrapper>

            <SliderWrapper>
              <HoverInput
                type="range"
                id="volume"
                name="volume"
                min={0}
                max={1}
                step="any"
                value={volume}
                onChange={handleVolumeChange}
                onTouchMove={handleVolumeTouchChange}
              />
            </SliderWrapper>
          </Row>
          <HoverInput
            type="range"
            id="progress"
            name="progress"
            min={0}
            max={0.999999}
            step="any"
            value={played}
            onMouseDown={handleSeekMouseDown}
            onChange={handleSeekChange}
            onMouseUp={handleSeekMouseUp}
            onTouchEnd={handleSeekTouchUp}
            onTouchMove={handleSeekTouchChange}
            onTouchStart={handleSeekMouseDown}
          />
        </ProgressRow>
        <VideosControlsTablet>
          <PlayerButton
            onMouseOver={() => {
              setPlayPauseIsHovered(true);
            }}
            onMouseOut={() => {
              setPlayPauseIsHovered(false);
            }}
            onClick={() => {
              playing ? setPlaying(false) : setPlaying(true);
            }}
          >
            {playing ? (
              <Icon
                name="event-pages_pause-button"
                color={
                  playPauseIsHovered ? theme.color.primary : theme.color.white
                }
              />
            ) : (
              <Icon
                name="event-pages_play-button"
                color={
                  playPauseIsHovered ? theme.color.primary : theme.color.white
                }
              />
            )}
          </PlayerButton>
          <PSmall>
            {`${formatDuration(played * duration)} / 
      ${formatDuration(duration)}`}
          </PSmall>
          <PlayerButton
            onMouseOver={() => {
              setFullscreenIsHovered(true);
            }}
            onMouseOut={() => {
              setFullscreenIsHovered(false);
            }}
            onClick={handleClickFullscreen}
          >
            <Icon
              name={
                screenfull.isFullscreen ? 'close_fullscreen' : 'open_fullscreen'
              }
              color={
                fullscreenIsHovered ? theme.color.primary : theme.color.white
              }
            />
          </PlayerButton>
        </VideosControlsTablet>
      </Column>
    );
  }, [
    duration,
    fullscreenIsHovered,
    handleClickFullscreen,
    playPauseIsHovered,
    played,
    playing,
    theme.color.primary,
    theme.color.white,
    volume,
    volumeIconName,
  ]);

  return (
    <>
      {isLoading ? (
        <LoadingFallBack />
      ) : (
        <>
          {videoItems?.length > 0 ? (
            <Column data-testid="video-player" style={{ width: '100%' }}>
              <VideoPlayerContainer
                onMouseEnter={() => {
                  setShowVideoDetails(true);
                }}
                onMouseLeave={() => {
                  setShowVideoDetails(false);
                }}
                ref={videoWrapperRef}
              >
                <ReactPlayer
                  ref={videoRef}
                  width="100%"
                  height="100%"
                  muted={volume === 0}
                  loop
                  playsinline
                  playing={playing}
                  pip={false}
                  url={selectedVideoItem?.link}
                  onPause={() => {
                    setPlaying(false);
                  }}
                  onPlay={() => {
                    setPlaying(true);
                    if (onPlayCallback) onPlayCallback();
                  }}
                  onProgress={handleProgress}
                  onDuration={duration => {
                    setDuration(duration);
                  }}
                  volume={volume}
                  light={getYouTubeThumbnail(videoItems?.[0])}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: '100%',
                    height: '100%',
                  }}
                  config={{
                    youtube: {
                      playerVars: {
                        enablejsapi: 1,
                        origin: window.location.origin,
                      },
                    },
                  }}
                />

                {(showVideoDetails || isTablet) &&
                  (!isMobile || (isFullscreen && isIos)) && (
                    <VideoDetails>
                      <VideoTitle>{selectedVideoItem?.title}</VideoTitle>
                      {isTablet ? (
                        videoControls
                      ) : (
                        <VideoControls>
                          <PlayerButton
                            onMouseOver={() => {
                              setPlayPauseIsHovered(true);
                            }}
                            onMouseOut={() => {
                              setPlayPauseIsHovered(false);
                            }}
                            onClick={() => {
                              playing ? setPlaying(false) : setPlaying(true);
                            }}
                          >
                            {playing ? (
                              <Icon
                                name="event-pages_pause-button"
                                color={
                                  playPauseIsHovered
                                    ? theme.color.primary
                                    : theme.color.white
                                }
                              />
                            ) : (
                              <Icon
                                name="event-pages_play-button"
                                color={
                                  playPauseIsHovered
                                    ? theme.color.primary
                                    : theme.color.white
                                }
                              />
                            )}
                          </PlayerButton>

                          <CenterItem>
                            <PSmall>
                              {`${formatDuration(played * duration)} /
                      ${formatDuration(duration)}`}
                            </PSmall>
                          </CenterItem>
                          <Row $gap="0.2rem">
                            <VolumeIconWrapper
                              onClick={() => {
                                setVolume(volume === 0 ? 1 : 0);
                              }}
                            >
                              <Icon
                                size={24}
                                name={volumeIconName}
                                color={theme.color.primary}
                              />
                            </VolumeIconWrapper>
                            <SliderWrapper>
                              <HoverInput
                                type="range"
                                id="volume"
                                name="volume"
                                min={0}
                                max={1}
                                step="any"
                                value={volume}
                                onChange={handleVolumeChange}
                              />
                            </SliderWrapper>
                          </Row>
                          <HoverInput
                            type="range"
                            id="progress"
                            name="progress"
                            min={0}
                            max={0.999999}
                            step="any"
                            value={played}
                            onMouseDown={handleSeekMouseDown}
                            onChange={handleSeekChange}
                            onMouseUp={handleSeekMouseUp}
                          />

                          <PlayerButton
                            onMouseOver={() => {
                              setFullscreenIsHovered(true);
                            }}
                            onMouseOut={() => {
                              setFullscreenIsHovered(false);
                            }}
                            onClick={handleClickFullscreen}
                          >
                            <Icon
                              name={
                                screenfull.isFullscreen
                                  ? 'close_fullscreen'
                                  : 'open_fullscreen'
                              }
                              color={
                                fullscreenIsHovered
                                  ? theme.color.primary
                                  : theme.color.white
                              }
                            />
                          </PlayerButton>
                        </VideoControls>
                      )}
                    </VideoDetails>
                  )}
              </VideoPlayerContainer>
              {isMobile && !isFullscreen && (
                <MobileVideoDetails>
                  <VideoTitle>{selectedVideoItem?.title}</VideoTitle>
                  {videoControls}
                </MobileVideoDetails>
              )}
            </Column>
          ) : (
            <NoEvents $color={textColor} data-testid="video-player">
              {t('no-pre-recorded-videos')}
            </NoEvents>
          )}
        </>
      )}
    </>
  );
};

export default VideoPlayer;
